// AddDemo.jsx
import {
  Autocomplete,
  Avatar,
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import {
  setLoader,
  setSnackBar,
  setSnackBarMessage,
  setSnackBarVariant,
} from '../../redux';
import { submitDemo } from '../../_services/creater/creatorDemo.service';
import { formSnackbar } from '../../_services/_constants/snackbar.constant';
import { useHistory } from 'react-router-dom';
import SnackbarContainer from '../Snackbar';
import ConfirmModal from '../../components/reusableComponents/ConfirmModal';
import { regexValidator } from '../../_helpers/reusablefunctions/regexValidator';
import { getGenre, getLanguage } from '../../_services/profile';
import ApiService from '../../_services/ApiService';
import FirebaseImageStore from '../../_helpers/reusablefunctions/firebaseImageStore';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SearchableSelect from '../../components/reusableComponents/SearchableSelect';

function AddDemo({ handleChange, editData }) {
  const userData = useSelector((state) => state.userData.userData);
  const dispatch = useDispatch();
  const history = useHistory();
  const mdScreen = useMediaQuery('(max-width:1024px)');
  const smScreen = useMediaQuery('(max-width:768px)');
  const [genreSearch, setGenreSearch] = useState("");
  const [moodSearch, setMoodSearch] = useState("");
  const [languageSearch, setLanguageSearch] = useState("");

  const editId = editData?.id || null; // demo id in edit mode

  const [data, setData] = useState({
    audio_name: '',
    description: '',
    demo_link: '',
    demoPoster_url: '',
    genre: '',
    mood: '',
    language: '',
    release_date: '',
  });

  const [errors, setErrors] = useState({
    audio_name: false,
    audio_name_helperTxt: null,
    description: false,
    description_helperTxt: null,
    demo_link: false,
    demo_link_helperTxt: null,
  });

  const [genreList, setGenreList] = useState([]);
  const [moodList, setMoodList] = useState([]);
  const [languageList, setLanguageList] = useState([]);

  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const posterInputRef = useRef();
  const today = new Date().toISOString().split('T')[0];

  const ref = useRef();

  const handleCancel = () => {
    setData({
      audio_name: '',
      description: '',
      demo_link: '',
      demoPoster_url: '',
      genre: '',
      mood: '',
      language: '',
      release_date: '',
    });
    setPosterFile(null);
    setPosterPreview(null);
  };

  // Fetch dropdowns
  useEffect(() => {
    async function fetchDropdowns() {
      const g = await getGenre();
      const m = await ApiService.get('getMood');
      const l = await getLanguage();

      setGenreList(Array.isArray(g?.data?.data) ? g.data.data : []);
      setMoodList(Array.isArray(m?.data?.data) ? m.data.data : []);
      setLanguageList(Array.isArray(l?.data?.data) ? l.data.data : []);
    }

    fetchDropdowns();
  }, []);

  // Prefill when editing
  useEffect(() => {
    if (!editData) return;

    setData({
      audio_name: editData.audio_name || '',
      description: editData.description || '',
      demo_link: editData.demo_link || '',
      demoPoster_url: editData.demoPoster_url || '',
      genre: editData.genre?.id || '',
      mood: editData.mood?.id || '',
      language: editData.language?.id || '',
      release_date: editData.release_date || '',
    });

    if (editData.demoPoster_url) {
      setPosterPreview(editData.demoPoster_url);
    }
  }, [editData]);

  // const handlePosterSelect = (event) => {
  //   const file = event.target?.files?.[0];

  //   if (!file) return;

  //   if (!file.type.startsWith('image/')) {
  //     dispatch(setSnackBar(true));
  //     dispatch(setSnackBarMessage('Please select a valid image file'));
  //     dispatch(setSnackBarVariant('error'));
  //     setTimeout(() => dispatch(setSnackBar(false)), 3000);
  //     return;
  //   }

  //   if (posterPreview) {
  //     URL.revokeObjectURL(posterPreview);
  //   }

  //   setPosterFile(file);
  //   const previewUrl = URL.createObjectURL(file);
  //   setPosterPreview(previewUrl);
  // };

  const handlePosterSelect = (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;

    // ✅ Allow only JPG & PNG
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      dispatch(setSnackBar(true));
      dispatch(setSnackBarMessage('Only JPG and PNG images are allowed'));
      dispatch(setSnackBarVariant('error'));
      setTimeout(() => dispatch(setSnackBar(false)), 3000);
      event.target.value = null;
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const { width, height } = img;

      // ✅ EXACT dimension check
      if (width !== 3000 || height !== 3000) {
        dispatch(setSnackBar(true));
        dispatch(
          setSnackBarMessage(
            'Image must be exactly 3000px × 3000px',
          ),
        );
        dispatch(setSnackBarVariant('error'));
        setTimeout(() => dispatch(setSnackBar(false)), 3000);

        URL.revokeObjectURL(objectUrl);
        event.target.value = null;
        return;
      }

      // ✅ Clean old preview
      if (posterPreview) {
        URL.revokeObjectURL(posterPreview);
      }

      setPosterFile(file);
      setPosterPreview(objectUrl);
    };

    img.onerror = () => {
      dispatch(setSnackBar(true));
      dispatch(setSnackBarMessage('Invalid image file'));
      dispatch(setSnackBarVariant('error'));
      setTimeout(() => dispatch(setSnackBar(false)), 3000);
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const uploadPosterToFirebase = () => {
    return new Promise((resolve, reject) => {
      // If no new file selected, keep existing URL (edit mode)
      if (!posterFile) {
        return resolve(data.demoPoster_url || '');
      }

      const firebase = FirebaseImageStore({
        selectedFile: posterFile,
        userData: userData,
        functionCall: ({ downloadURL }) => {
          resolve(downloadURL);
        },
      });

      firebase.saveAvatar().catch(reject);
    });
  };

  // Optional: warn on refresh / back
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      const confirmationMessage = 'Are you sure you want to leave?';
      event.returnValue = confirmationMessage;
      if (!window.confirm()) {
        event.preventDefault();
      } else {
        history.push('/submitdemo');
      }
    };

    const handlePopState = () => {
      const confirmationMessage = 'Are you sure you want to leave?';
      if (window.confirm()) {
        // allow leaving
      } else {
        window.history.pushState(null, null, window.location.pathname);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [history]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setData((prev) => ({
      ...prev,
      [name]: value,
    }));

    switch (name) {
      case 'audio_name': {
        const isValid = regexValidator(value, 'alphanumericEndSpace');
        if (!isValid) {
          setErrors((prev) => ({
            ...prev,
            audio_name: true,
            audio_name_helperTxt: formSnackbar.errors.validInput,
          }));
        } else if (value.length > 100) {
          setErrors((prev) => ({
            ...prev,
            audio_name: true,
            audio_name_helperTxt:
              formSnackbar.errors.characterCountErrorlow,
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            audio_name: false,
            audio_name_helperTxt: null,
          }));
        }
        break;
      }
      case 'description': {
        const isValid = regexValidator(value, 'descriptionText');
        if (!isValid) {
          setErrors((prev) => ({
            ...prev,
            description: true,
            description_helperTxt: formSnackbar.errors.validInput,
          }));
        } else if (value.length > 1000) {
          setErrors((prev) => ({
            ...prev,
            description: true,
            description_helperTxt:
              formSnackbar.errors.characterCountErrorMaxOnek,
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            description: false,
            description_helperTxt: null,
          }));
        }
        break;
      }
      case 'demo_link': {
        const pattern =
          /^(https?:\/\/)?(www\.)?(soundcloud\.com|drive\.google\.com)/i;
        if (!pattern.test(value)) {
          setErrors((prev) => ({
            ...prev,
            demo_link: true,
            demo_link_helperTxt:
              'Only SoundCloud or Google Drive links are allowed',
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            demo_link: false,
            demo_link_helperTxt: null,
          }));
        }
        break;
      }
      default:
        break;
    }
  };

  async function saveDemo(e) {
    e.preventDefault();
    dispatch(setLoader(true));

    let posterURL = data.demoPoster_url;
    try {
      posterURL = await uploadPosterToFirebase();
    } catch (err) {
      console.error(err);
      dispatch(setLoader(false));
      dispatch(setSnackBar(true));
      dispatch(setSnackBarVariant('error'));
      dispatch(setSnackBarMessage('Failed to upload poster'));
      setTimeout(() => dispatch(setSnackBar(false)), 3000);
      return;
    }

    dispatch(setSnackBar(true));
    dispatch(setSnackBarVariant('info'));
    dispatch(setSnackBarMessage(formSnackbar.submitDemo.DemoUpload));

    const postData = {
      ...data,
      demoPoster_url: posterURL,
      userId: userData?.id,
    };

    // For POST we send FormData directly
    const formdata = new FormData();
    Object.keys(postData).forEach((key) => {
      formdata.append(key, postData[key]);
    });

    let response;
    try {
      if (editId) {
        // For PUT we pass an object with itemId + formdata,
        // and adjust service to use postData.formdata as body.
        response = await submitDemo(formdata, 'PUT', editId);
      } else {
        response = await submitDemo(formdata, 'POST');
      }
    } catch (err) {
      console.error(err);
      response = err?.response;
    }

    if (response && (response.status === 200 || response.status === 201)) {
      dispatch(setSnackBarVariant('success'));
      dispatch(
        setSnackBarMessage(
          editId
            ? formSnackbar.submitDemo.DemoUpdateSuccess ||
            'Demo Updated Successfully'
            : formSnackbar.submitDemo.DemoUploadSuccess,
        ),
      );
      setTimeout(() => dispatch(setSnackBar(false)), 2000);
      handleChange(); // back to list
    } else if (response && response.status === 400) {
      dispatch(
        setSnackBarMessage(
          response.data?.error?.[0] || 'Validation error',
        ),
      );
      dispatch(setSnackBarVariant('error'));
      setTimeout(() => {
        dispatch(setSnackBar(false));
      }, 2000);
    } else {
      dispatch(setSnackBarVariant('error'));
      dispatch(
        setSnackBarMessage(
          formSnackbar.serviceRequest.serviceRequestFailure ||
          'Something went wrong',
        ),
      );
      setTimeout(() => dispatch(setSnackBar(false)), 3000);
    }

    dispatch(setLoader(false));
  }

  const handleRouteBack = () => {
    if (
      data.audio_name !== '' ||
      data.description !== '' ||
      data.demo_link !== ''
    ) {
      ref.current?.handleOpen();
    } else {
      handleChange();
    }
  };

  const confirmHandler = () => {
    handleChange();
  };

  return (
    <div>
      <div className="p-3">
        <button className="icononly-btn" onClick={handleRouteBack}>
          <ArrowBackIcon /> Back
        </button>
      </div>
      <div>
        <Box sx={{ width: '90%', margin: 'auto' }} className="pb-5">
          <div className="card account-bg">
            <div
              className="card-body border rounded"
              style={{ backgroundColor: '#fff' }}
            >
              <p className="cardheading">
                {editId
                  ? 'Edit your masterpiece'
                  : 'Submit your masterpiece'}
              </p>

              {/* Poster Image */}
              <FormControl fullWidth className="mt-3">
                <p className="mb-1 fw-bold">Poster Image *</p>

                <Box
                  onClick={() =>
                    posterInputRef.current &&
                    posterInputRef.current.click()
                  }
                  sx={{
                    position: 'relative',
                    width: 120,
                    height: 120,
                    cursor: 'pointer',
                  }}
                >
                  <Avatar
                    src={posterPreview}
                    alt="Poster"
                    sx={{
                      width: 120,
                      height: 120,
                      border: '2px solid #ddd',
                      bgcolor: '#f0f0f0',
                    }}
                  />

                  <CameraAltIcon
                    size={20}
                    style={{
                      position: 'absolute',
                      bottom: 5,
                      right: 5,
                      background: '#fff',
                      borderRadius: '50%',
                      padding: 4,
                    }}
                  />

                  <input
                    type="file"
                    ref={posterInputRef}
                    accept="image/*"
                    onChange={handlePosterSelect}
                    style={{ display: 'none' }}
                  />
                </Box>
              </FormControl>

              {/* Link */}
              <FormControl className="mt-3" fullWidth>
                <TextField
                  fullWidth
                  value={data.demo_link}
                  id="outlined-required"
                  label="Link*"
                  name="demo_link"
                  error={errors.demo_link}
                  helperText={errors.demo_link_helperTxt}
                  onChange={handleInputChange}
                />
              </FormControl>
              <Typography sx={{ fontSize: '13px', color: '#555' }}>
                <strong>Note:</strong> Only SoundCloud and Google Drive
                links are supported.
              </Typography>

              {/* Song Name */}
              <FormControl className="mt-4" fullWidth>
                <TextField
                  fullWidth
                  value={data.audio_name}
                  id="outlined-required"
                  label="Song Name*"
                  name="audio_name"
                  error={errors.audio_name}
                  helperText={errors.audio_name_helperTxt}
                  onChange={handleInputChange}
                />
              </FormControl>

              {/* Description */}
              <FormControl className="mt-4" fullWidth>
                <TextField
                  id="outlined-multiline-static"
                  label="Describe your interest in music*"
                  name="description"
                  multiline
                  type="text"
                  error={errors.description}
                  helperText={errors.description_helperTxt}
                  value={data.description}
                  rows={8}
                  sx={{ width: '100%' }}
                  onChange={handleInputChange}
                />
              </FormControl>

              {/* Release Date / Genre / Mood / Language */}
              <div className="row" style={{ marginTop: '16px' }}>
                <div className="col-12 col-md-6 mb-3">
                  <TextField
                    fullWidth
                    label="Release Date *"
                    name="release_date"
                    type="date"
                    value={data.release_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: today, // ✅ disables previous dates
                    }}
                  />
                </div>

                <div className="col-12 col-md-6 mb-3">
                  <SearchableSelect
                    label="Genre"
                    name="genre"
                    value={data.genre}
                    options={genreList}
                    displayKey="genre"
                    valueKey="id"
                    onChange={(e) => setData(prev => ({ ...prev, genre: e.target.value }))}
                    showClear={true}
                    required
                  />
                </div>

                <div className="col-12 col-md-6 mb-3">
                  <SearchableSelect
                    label="Mood"
                    name="mood"
                    value={data.mood}
                    options={moodList}
                    displayKey="mood"
                    valueKey="id"
                    required
                    onChange={(e) => setData(prev => ({ ...prev, mood: e.target.value }))}
                    showClear={true}
                  />
                </div>

                <div className="col-12 col-md-6 mb-3">
                  <SearchableSelect
                    label="Language"
                    name="language"
                    value={data.language}
                    options={languageList}
                    displayKey="language"
                    valueKey="id"
                    required
                    onChange={(e) => setData(prev => ({ ...prev, language: e.target.value }))}
                    showClear={true}
                  />
                </div>
              </div>

              {/* Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: '30px',
                }}
              >
                <Box sx={{ flex: '1 1 auto' }} />
                <button
                  className="gn-actionbtn"
                  style={{ width: '110px' }}
                  onClick={saveDemo}
                  disabled={
                    !data.audio_name ||
                    !data.demo_link ||
                    !data.description ||
                    !data.release_date ||
                    !data.genre ||
                    !data.mood ||
                    !data.language ||
                    (!posterFile &&
                      !posterPreview &&
                      !data.demoPoster_url) ||
                    errors.demo_link ||
                    errors.audio_name ||
                    errors.description
                  }
                >
                  {editId ? 'Update' : 'Submit'}
                </button>
              </Box>
            </div>
          </div>
        </Box>
        <div>
          <SnackbarContainer />
          <ConfirmModal confirmHandler={confirmHandler} ref={ref} />
        </div>
      </div>
    </div>
  );
}

export default AddDemo;
